import React, { useCallback, useContext, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import urlJoin from 'url-join';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from 'Auth';
import {
  AnimatedScreen,
  LoadingPage,
  PageHeader,
  EmptyTable,
  Table,
  DEFAULT_RESULTS_PER_PAGE,
  Paginate,
  PaginationType,
} from 'Shared/components';
import { Can } from 'Shared/utils/auth';
import { isMissing, isPresent } from 'Shared/utils/object';
import { createPartnerPolicy } from 'Admin/authPolicies';
import {
  AuthorizationsToSellType,
  formatAuthorizedStates,
  PartnerContext,
} from 'Admin/utils';

import {
  PartnerFilters,
  FilterType,
} from 'Admin/Partners/components/PartnerFilters';
import { GET_PARTNER_AGENCIES_QUERY } from 'Admin/Partners/graphql';

type Data = {
  partners?: {
    pagination: PaginationType;
    records: Array<{
      id: string;
      name: string;
      licensedAgency: {
        employerIdentificationNumber: string;
        nationalProducerNumber: string;
        authorizationsToSell: Array<AuthorizationsToSellType>;
      } | null;
    }>;
  };
};

export const PartnerAgencies: React.FC<RouteComponentProps> = ({ history }) => {
  const { user } = useAuth0();
  const {
    loading: loadingPartner,
    error: partnerError,
    currentPartner,
  } = useContext(PartnerContext);
  const [resultsPerPage, changeResultsPerPage] = useState(
    DEFAULT_RESULTS_PER_PAGE
  );
  const [filters, setFilters] = useState<FilterType>({});

  const { networkStatus, error, data, fetchMore } = useQuery<Data>(
    GET_PARTNER_AGENCIES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        paginate: {
          limit: resultsPerPage,
          offset: 0,
        },
        parentId: currentPartner && currentPartner.id,
        filters: filters,
      },
      skip:
        loadingPartner || isPresent(partnerError) || isMissing(currentPartner),
    }
  );

  const onChangeFilters = useCallback((newFilters: FilterType) => {
    setFilters(newFilters);
  }, []);

  if (loadingPartner) return <LoadingPage />;

  const partners = data?.partners?.records;
  const pagination = data?.partners?.pagination;

  return (
    <AnimatedScreen
      testId="partner-agencies"
      loading={networkStatus === 1}
      error={error && error.message}
    >
      <Can
        grants={user!.effectiveGrants}
        policy={createPartnerPolicy}
        yes={() => (
          <PageHeader
            onActionClick={() => {
              history.push(`${history.location.pathname}/create`);
            }}
            actionTestId="create-agency-button"
            actionName="Create Agency"
            heading="Agencies"
          />
        )}
        no={() => <PageHeader heading="Agencies" />}
      />

      <PartnerFilters filters={filters} onChangeFilters={onChangeFilters} />

      {partners && partners.length ? (
        <>
          <Table
            headers={['Agency Name', 'EIN', 'NPN', 'Active States']}
            columnWidths={[2, 1, 1, 2]}
            name="partner-agencies-table"
            rows={partners.map((partner) => ({
              key: partner.id,
              onViewRow: () =>
                history.push(urlJoin('/admin/partners/', partner.id)),
              data: [
                partner.name,
                partner.licensedAgency?.employerIdentificationNumber,
                partner.licensedAgency?.nationalProducerNumber,
                partner.licensedAgency?.authorizationsToSell &&
                  formatAuthorizedStates(
                    partner.licensedAgency.authorizationsToSell
                  ),
              ],
            }))}
            loading={[2, 3, 4].includes(networkStatus)}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              fetchMore({
                variables: { paginate: { offset, limit } },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    ...prev,
                    ...fetchMoreResult,
                  };
                },
              });
            }}
            onLimitChange={changeResultsPerPage}
          />
        </>
      ) : (
        <EmptyTable>
          No Agencies exist for{' '}
          {currentPartner ? currentPartner.name : 'current partner'}
        </EmptyTable>
      )}
    </AnimatedScreen>
  );
};
